import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  Snackbar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { formatTimeSpan } from '../utils/formatters';
import { Add as AddIcon, Place as PlaceIcon } from '@mui/icons-material';
import api from '../services/api';
import schedulesService from '../services/schedulesService';
import placeServicesService from '../services/placeServicesService';
import placesService from '../services/placesService';
import { useSchedules } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useContextualHint } from '../hooks/useContextualHint';
import { useThemeCustom } from '../hooks/useThemeCustom';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import ContextualHint from '../components/ContextualHint';
import DeleteConfirmDialog from '../components/ui/DeleteConfirmDialog';
import ScheduleForm from '../components/forms/ScheduleForm';
import ScheduleFilters from '../components/ScheduleFilters';
import ScheduleCard from '../components/ScheduleCard';
import ShareScheduleDialog from '../components/ShareScheduleDialog';
import ShiftForm from '../components/ShiftForm';
import ScheduleCreationWizard from '../components/schedules/ScheduleCreationWizard';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import { trackEvent, trackError, trackFormAbandonment } from '../utils/tracking';

export default function SchedulesPage() {
  console.log('[SchedulesPage] Componente renderizado - timestamp:', Date.now());
  
  // 🔍 TESTE: Importar com safe fallback e logs
  let getLabel;
  try {
    const themeContext = useThemeCustom();
    getLabel = themeContext.getLabel;
    console.log('[SchedulesPage] ✅ getLabel obtido do hook com sucesso');
  } catch (error) {
    console.warn('[SchedulesPage] ⚠️ Erro ao obter getLabel do hook:', error.message);
    getLabel = (key) => {
      const fallbacks = { 
        schedules: 'Horários',
        schedulesDescription: 'Gerencie seus horários'
      };
      console.debug(`[SchedulesPage.getLabel] Fallback para "${key}"`);
      return fallbacks[key] || key;
    };
  }
  
  // ✅ Custom Hooks - React Query
  const { 
    schedules, 
    professionals, 
    places, 
    loading, 
    error, 
    reload, 
    create, 
    update, 
    deleteItem 
  } = useSchedules();
  
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const shiftDialog = useDialog();
  const shareDialog = useDialog();
  const { snackbar, showSuccess, showError, showWarning, hideSnackbar } = useSnackbar();

  // Estados adicionais específicos de schedules
  const [placeProfessionals, setPlaceProfessionals] = useState([]);
  const [ScheduleShifts, setAgendaShifts] = useState({});
  const [availablePlaceServices, setAvailablePlaceServices] = useState([]);
  const [shareUrl, setShareUrl] = useState('');
  
  // Estado para wizard de criação de agenda
  const [showCreationWizard, setShowCreationWizard] = useState(false);
  const [selectedPlaceForWizard, setSelectedPlaceForWizard] = useState(null);
  const [showPlaceSelector, setShowPlaceSelector] = useState(false);
  
  // Estado para diálogo de agendamento
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentPrefillConfig, setAppointmentPrefillConfig] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterPlace, setFilterPlace] = useState('');

  // Obter dica contextual
  const hint = useContextualHint('schedules', { items: schedules || [], shifts: ScheduleShifts });

  // Carregar turnos quando schedules mudar
  useEffect(() => {
    if (schedules?.length > 0) {
      console.log('[SchedulesPage] Carregando turnos para', schedules.length, 'schedules');
      loadShiftsForAgendas(schedules);
    }
  }, [schedules]);

  const loadAllPlaceProfessionals = async (placesData, SchedulesData, professionalsData) => {
    console.log('[SchedulesPage] Criando placeProfessionals a partir de', placesData.length, 'locais');
    try {
      const allPlaceProfessionals = [];
      
      // Criar placeProfessionals a partir dos professionalIds de cada place
      placesData.forEach(place => {
        if (place.professionalIds && place.professionalIds.length > 0) {
          place.professionalIds.forEach(profId => {
            const professional = professionalsData.find(p => p.id === profId);
            if (professional) {
              // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
              const existingAgenda = SchedulesData.find(
                a => a.placeId === place.id && a.professionalId === profId
              );
              
              allPlaceProfessionals.push({
                id: existingAgenda?.placeProfessionalId || `${place.id}-${profId}`, // ID real ou temporário
                placeId: place.id,
                placeName: place.name,
                professionalId: profId,
                professionalName: professional.name,
                isActive: existingAgenda?.isActive ?? true
              });
            }
          });
        }
      });
      
      console.log('[SchedulesPage] Total de placeProfessionals criados:', allPlaceProfessionals.length);
      setPlaceProfessionals(allPlaceProfessionals);
    } catch (error) {
      console.error('[SchedulesPage] Erro ao criar placeProfessionals:', error);
      setPlaceProfessionals([]);
    }
  };

  const loadShiftsForAgendas = async (SchedulesData) => {
    try {
      const shiftsMap = {};
      await Promise.all(
        SchedulesData.map(async (schedule) => {
          try {
            const shifts = await schedulesService.getShifts(schedule.id);
            shiftsMap[schedule.id] = shifts;
          } catch (error) {
            console.error(`Erro ao carregar turnos da schedule ${schedule.id}:`, error);
            shiftsMap[schedule.id] = [];
          }
        })
      );
      setAgendaShifts(shiftsMap);
    } catch (error) {
      console.error('Erro ao carregar turnos:', error);
    }
  };

  // ✅ Handlers de CRUD
  // ✅ Callbacks memoizados para cards
  const handleEditAgenda = useCallback((schedule) => {
    formDialog.handleOpen(schedule);
  }, [formDialog]);

  const handleDeleteAgenda = useCallback((schedule) => {
    deleteDialog.handleOpen(schedule);
  }, [deleteDialog]);

  const handleShareAgenda = useCallback((schedule) => {
    const shifts = ScheduleShifts[schedule.id] || [];
    
    if (shifts.length === 0) {
      showWarning('Esta schedule precisa ter turnos e horários configurados para poder ser compartilhada.');
      return;
    }
    
    const url = `https://share.agendario.com.br/schedule/${schedule.id}`;
    setShareUrl(url);
    shareDialog.handleOpen(schedule);
  }, [ScheduleShifts, showWarning, shareDialog]);

  const handleCreate = async (data) => {
    try {
      const result = await create(data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_created', {
        agenda_id: result.id || 'unknown',
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead,
        max_available_days_to_show: data.maxAvailableDaysToShow
      });
      
      showSuccess('schedule criada com sucesso!');
      await loadShiftsForAgendas(schedules);
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: false,
        professional_id: data.professionalId,
        place_id: data.placeId
      });
      showError('Erro ao criar schedule: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_updated', {
        agenda_id: formDialog.data.id,
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead
      });
      
      showSuccess('schedule atualizada com sucesso!');
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: true,
        agenda_id: formDialog.data.id
      });
      showError('Erro ao atualizar schedule: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handleDelete = async () => {
    console.log('[SchedulesPage] handleDelete INICIADO');
    console.log('[SchedulesPage] deleteDialog.data:', deleteDialog.data);
    console.log('[SchedulesPage] deleteItem type:', typeof deleteItem);
    console.log('[SchedulesPage] deleteItem:', deleteItem);
    
    try {
      console.log('[SchedulesPage] Tentando excluir schedule ID:', deleteDialog.data?.id);
      const ScheduleName = deleteDialog.data?.name;
      const ScheduleId = deleteDialog.data?.id;
      console.log('[SchedulesPage] Nome da schedule:', ScheduleName);
      console.log('[SchedulesPage] ID da schedule:', ScheduleId);
      
      if (typeof deleteItem !== 'function') {
        console.error('[SchedulesPage] deleteItem NÃO É UMA FUNÇÃO!');
        throw new Error('deleteItem não é uma função válida');
      }
      
      console.log('[SchedulesPage] Chamando deleteItem com ID:', ScheduleId);
      const result = await deleteItem(ScheduleId);
      console.log('[SchedulesPage] deleteItem concluído com sucesso. Result:', result);
      
      console.log('[SchedulesPage] Fechando dialog...');
      deleteDialog.handleClose();
      console.log('[SchedulesPage] Dialog fechado');
      
      console.log('[SchedulesPage] Tracking evento...');
      trackEvent('agenda_deleted', {
        agenda_id: deleteDialog.data.id,
        agenda_name: ScheduleName
      });
      
      console.log('[SchedulesPage] Mostrando mensagem de sucesso...');
      showSuccess('schedule excluída com sucesso!');
      console.log('[SchedulesPage] handleDelete FINALIZADO COM SUCESSO');
    } catch (err) {
      console.error('[SchedulesPage] ERRO em handleDelete:', err);
      console.error('[SchedulesPage] Erro stack:', err.stack);
      console.error('[SchedulesPage] Erro response:', err.response);
      
      trackError('agenda_delete_error', err, {
        agenda_id: deleteDialog.data?.id
      });
      showError('Erro ao excluir schedule: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('Link copiado com sucesso!');
    } catch (error) {
      showError('Erro ao copiar link');
    }
  };

  const handleOpenUrl = () => {
    window.open(shareUrl, '_blank');
  };

  // ✅ Handlers de Turnos (Shifts)
  const handleOpenShift = async (schedule, shift = null) => {
    try {
      // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
      const placeId = schedule.placeId;
      const professionalId = schedule.professionalId;
      const placeProfessionalId = schedule.placeProfessionalId;
      
      if (!placeId) {
        showError('Erro: Local não identificado na schedule');
        return;
      }
      
      if (!professionalId) {
        showError('Erro: Profissional não identificado na schedule');
        return;
      }
      
      // Buscar TODOS os serviços do local
      const allPlaceServices = await placeServicesService.getByPlaceId(placeId);
      
      // Buscar os serviços que o profissional pode realizar
      // Fazemos isso percorrendo cada placeService e buscando se o profissional está vinculado
      const professionalPlaceServices = [];
      
      for (const placeService of allPlaceServices) {
        try {
          // IMPORTANTE: usar placeService.id (placeServiceId), não serviceId
          // A rota é: /places/{placeId}/services/{placeServiceId}/professionals
          const professionals = await api.get(`/places/${placeId}/services/${placeService.id}/professionals`);
          const professionalsData = professionals.data?.$values || professionals.data || [];
          
          // Verificar se o profissional da schedule está nessa lista
          const isProfessionalLinked = professionalsData.some(
            p => p.professionalId === professionalId || p.placeProfessionalId === placeProfessionalId
          );
          
          if (isProfessionalLinked) {
            professionalPlaceServices.push(placeService);
          }
        } catch (error) {
          console.warn(`Erro ao verificar profissionais do placeService ${placeService.id}:`, error);
          // Continua para próximo serviço
        }
      }
      
      console.log('[SchedulesPage] Serviços filtrados para profissional:', {
        total: allPlaceServices.length,
        filtrados: professionalPlaceServices.length,
        professionalId,
        placeId
      });
      
      setAvailablePlaceServices(professionalPlaceServices);
      shiftDialog.handleOpen({ ...shift, ScheduleId: schedule.id, schedule });
    } catch (error) {
      console.error('Erro ao carregar serviços do local:', error);
      setAvailablePlaceServices([]);
      showError('Erro ao carregar serviços oferecidos no local. Verifique se o local possui serviços configurados.');
    }
  };

  const handleShiftSubmit = async (shiftData) => {
    try {
      const ScheduleId = shiftDialog.data.ScheduleId;
      const shiftId = shiftDialog.data.id;
      
      if (shiftId) {
        await schedulesService.updateShift(ScheduleId, shiftId, shiftData);
        showSuccess('Turno atualizado com sucesso!');
      } else {
        await schedulesService.createShift(ScheduleId, shiftData);
        showSuccess('Turno criado com sucesso!');
      }
      
      shiftDialog.handleClose();
      await loadShiftsForAgendas(schedules);
    } catch (error) {
      showError('Erro ao salvar turno: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleShiftDelete = async (shiftId) => {
    try {
      const ScheduleId = shiftDialog.data.ScheduleId;
      await schedulesService.deleteShift(ScheduleId, shiftId);
      showSuccess('Turno excluído com sucesso!');
      shiftDialog.handleClose();
      await loadShiftsForAgendas(schedules);
    } catch (error) {
      showError('Erro ao excluir turno: ' + (error.response?.data?.message || error.message));
    }
  };

  // ✅ Helpers
  const getProfessionalName = (professionalId) => {
    return professionals.find(p => p.id === professionalId)?.name || 'N/A';
  };

  const getPlaceName = (placeId) => {
    return places.find(p => p.id === placeId)?.name || 'N/A';
  };

  const formatShiftDays = (shift) => {
    const days = [];
    if (shift.monday) days.push('Seg');
    if (shift.tuesday) days.push('Ter');
    if (shift.wednesday) days.push('Qua');
    if (shift.thursday) days.push('Qui');
    if (shift.friday) days.push('Sex');
    if (shift.saturday) days.push('Sáb');
    if (shift.sunday) days.push('Dom');
    return days.join(', ');
  };

  // Alias para manter compatibilidade com código existente (props passadas para componentes)
  const formatTime = formatTimeSpan;

  // ✅ Filtros memoizados
  const filteredAgendas = useMemo(() => {
    return schedules.filter((schedule) => {
      const matchesSearchTerm = !searchTerm || 
        schedule.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // ✅ NORMALIZADO: schedule tem professionalId e placeId diretamente
      const matchesProfessional = !filterProfessional || 
        schedule.professionalId === filterProfessional;
      
      const matchesPlace = !filterPlace || 
        schedule.placeId === filterPlace;
    
      return matchesSearchTerm && matchesProfessional && matchesPlace;
    });
  }, [schedules, searchTerm, filterProfessional, filterPlace]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterProfessional('');
    setFilterPlace('');
  };

  // ✅ Handlers para wizard de criação de agenda
  const handleOpenCreationWizard = useCallback((place) => {
    console.log('[SchedulesPage] Abrindo wizard de criação para local:', place);
    setSelectedPlaceForWizard(place);
    setShowCreationWizard(true);
  }, []);

  const handleCloseCreationWizard = useCallback(() => {
    console.log('[SchedulesPage] Fechando wizard de criação');
    setShowCreationWizard(false);
    setSelectedPlaceForWizard(null);
  }, []);

  const handleCreationWizardSuccess = useCallback((createdSchedules) => {
    console.log('[SchedulesPage] Agendas criadas com sucesso:', createdSchedules);
    setShowCreationWizard(false);
    setSelectedPlaceForWizard(null);
    showSuccess(`${createdSchedules.length} agenda(s) criada(s) com sucesso!`);
    reload(); // Recarregar lista de agendas
  }, [showSuccess, reload]);

  // Handler para abrir diálogo de agendamento a partir de um schedule
  const handleOpenAppointmentFromSchedule = useCallback((schedule) => {
    console.log('[SchedulesPage] Abrindo agendamento para schedule:', schedule);
    setAppointmentPrefillConfig({
      professionalId: schedule.professionalId,
      placeId: schedule.placeId,
    });
    setAppointmentDialogOpen(true);
  }, []);

  // Handler para fechar diálogo de agendamento
  const handleCloseAppointmentDialog = useCallback(() => {
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, []);

  // Handler para sucesso do agendamento
  const handleAppointmentSuccess = useCallback((appointment) => {
    console.log('[SchedulesPage] Agendamento criado com sucesso:', appointment);
    showSuccess('Agendamento criado com sucesso!');
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, [showSuccess]);

  // Obter profissionais de um local (com serviceIds do vínculo PlaceProfessional)
  const getProfessionalsForPlace = useCallback((placeId) => {
    // Buscar o place pelo ID
    const place = places.find(p => p.id === placeId);
    if (!place || !place.professionalIds) {
      return [];
    }
    
    // Mapear professionalIds para objetos de profissional com serviceIds
    return place.professionalIds
      .map(profId => {
        const prof = professionals.find(p => p.id === profId);
        // Buscar serviceIds do vínculo PlaceProfessional (padrão BFF normalizado)
        const placeProfLink = place.professionals?.find(pp => pp.professionalId === profId);
        return prof ? { 
          id: prof.id, 
          name: prof.name,
          serviceIds: placeProfLink?.serviceIds || []
        } : null;
      })
      .filter(Boolean);
  }, [places, professionals]);

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando schedules..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {getLabel('schedules')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowPlaceSelector(true)}
          disabled={places.length === 0}
        >
          Nova Agenda
        </Button>
      </Box>
      {/* Dica Contextual */}
      <ContextualHint hint={hint} onActionClick={() => formDialog.handleOpen(null)} />

      {/* Filtros */}
      <ScheduleFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterProfessional={filterProfessional}
        onProfessionalChange={setFilterProfessional}
        filterPlace={filterPlace}
        onPlaceChange={setFilterPlace}
        professionals={professionals}
        places={places}
        onClearFilters={clearFilters}
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Configure schedules automáticas vinculando profissionais a locais.
          A geração automática cria horários disponíveis com base nos turnos configurados.
        </Typography>
      </Alert>

      {/* Empty State */}
      {filteredAgendas.length === 0 ? (
        <EmptyState
          icon={() => <Typography fontSize={64}>📅</Typography>}
          title={schedules.length === 0 ? 'Nenhuma agenda cadastrada' : 'Nenhuma agenda encontrada'}
          message={
            schedules.length === 0 
              ? 'Clique em "Nova Agenda" para começar.' 
              : 'Tente ajustar os filtros para encontrar suas agendas.'
          }
          action={
            schedules.length === 0 && places.length > 0 && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowPlaceSelector(true)}>
                Nova Agenda
              </Button>
            )
          }
        />
      ) : (
        <Grid container spacing={3}>
          {filteredAgendas.map((schedule) => (
            <Grid item xs={12} sm={6} md={4} key={schedule.id}>
              <ScheduleCard
                schedule={schedule}
                professionalName={getProfessionalName(schedule.professionalId)}
                placeName={getPlaceName(schedule.placeId)}
                shifts={ScheduleShifts[schedule.id] || []}
                onEdit={handleEditAgenda}
                onDelete={handleDeleteAgenda}
                onShare={handleShareAgenda}
                onAddShift={() => handleOpenShift(schedule)}
                onEditShift={(shift) => handleOpenShift(schedule, shift)}
                onAppointment={handleOpenAppointmentFromSchedule}
                formatShiftDays={formatShiftDays}
                formatTime={formatTime}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* schedule Form Dialog */}
      <ScheduleForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
        professionals={professionals}
        places={places}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onConfirm={handleDelete}
        title="Confirmar Exclusão de Agenda"
        itemName={deleteDialog.data?.name}
        itemType="agenda"
        severity="error"
        impacts={[
          'Todos os turnos configurados serão removidos',
          'Todos os horários (time slots) serão excluídos',
          'Agendamentos futuros vinculados a esta agenda serão afetados',
          'O link de compartilhamento público deixará de funcionar',
          'Configurações de serviços e disponibilidade serão perdidas'
        ]}
      />

      {/* Share Dialog */}
      <ShareScheduleDialog
        open={shareDialog.open}
        onClose={shareDialog.handleClose}
        shareUrl={shareUrl}
        onCopy={handleCopyUrl}
        onOpen={handleOpenUrl}
      />

      {/* Shift Form Dialog */}
      <ShiftForm
        open={shiftDialog.open}
        onClose={shiftDialog.handleClose}
        onSubmit={handleShiftSubmit}
        onDelete={handleShiftDelete}
        shift={shiftDialog.data}
        ScheduleId={shiftDialog.data?.ScheduleId}
        availablePlaceServices={availablePlaceServices}
      />

      {/* Dialog de seleção de local para nova agenda */}
      <Dialog 
        open={showPlaceSelector} 
        onClose={() => setShowPlaceSelector(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Selecione o Local</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Escolha o local onde deseja criar agendas de atendimento:
          </Typography>
          <List>
            {places.map(place => {
              const profs = getProfessionalsForPlace(place.id);
              return (
                <ListItem key={place.id} disablePadding>
                  <ListItemButton 
                    onClick={() => {
                      setShowPlaceSelector(false);
                      handleOpenCreationWizard({
                        ...place,
                        professionals: profs,
                      });
                    }}
                    disabled={profs.length === 0}
                  >
                    <ListItemIcon>
                      <PlaceIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={place.name}
                      secondary={profs.length > 0 
                        ? `${profs.length} profissional(is): ${profs.map(p => p.name).join(', ')}`
                        : 'Nenhum profissional vinculado'
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          {places.length === 0 && (
            <Alert severity="warning">
              Você precisa criar um local primeiro antes de criar agendas.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPlaceSelector(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Wizard de criação de agenda */}
      {showCreationWizard && selectedPlaceForWizard && (
        <ScheduleCreationWizard
          open={showCreationWizard}
          onClose={handleCloseCreationWizard}
          onSuccess={handleCreationWizardSuccess}
          placeName={selectedPlaceForWizard.name}
          placeId={selectedPlaceForWizard.id}
          professionals={selectedPlaceForWizard.professionals || []}
        />
      )}

      {/* Diálogo de Agendamento - auto-suficiente, carrega próprios dados */}
      <AppointmentFormDialog
        open={appointmentDialogOpen}
        onClose={handleCloseAppointmentDialog}
        onSuccess={handleAppointmentSuccess}
        prefillConfig={appointmentPrefillConfig}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
